import React, { forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import HardBreak from '@tiptap/extension-hard-break';
import History from '@tiptap/extension-history';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { PredictionExtension, isCaretAtDocEnd } from './PredictionExtension';
import { DraftHelpers } from '../../../../util/Draft';

// Plain message-composition editor (toolbar hidden, no rich-text keyboard
// shortcuts) that replaces the draft-js/react-draft-wysiwyg <Editor>. Content
// is HTML in / HTML out. The NewMessage class component drives it imperatively
// through the ref handle (focus/insert/get/prediction), mirroring how it drove
// the old draft-js EditorState + this.domEditor ref.
const MessageEditor = forwardRef(
  (
    {
      html = '',
      placeholder = '',
      onChange = () => {},
      onFocus = () => {},
      onBlur = () => {},
      onKeyDown = () => false,
      onPaste = () => false,
      className,
      editorClassName,
    },
    ref
  ) => {
    // Last html the editor emitted or was synced to; used to distinguish an
    // external content change from the editor's own normalized output.
    const lastHtmlRef = React.useRef(html || '');
    const editor = useEditor({
      extensions: [
        Document,
        Paragraph,
        Text,
        HardBreak,
        History,
        // Preserve links/images through getHTML() so macro content (and any
        // href/img the agent keeps) survives edits and reaches the customer.
        // Mirrors the old draft-js behaviour (target=_blank, no navigate-on-click).
        Link.configure({
          autolink: true,
          openOnClick: false,
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        }),
        Image.configure({ allowBase64: true }),
        Placeholder.configure({ placeholder }),
        PredictionExtension,
      ],
      content: html || '',
      editorProps: {
        attributes: {
          class: editorClassName || 'msg-editor',
          'data-testid': 'message-editor',
          // Expose as an accessible textbox so screen readers (and tests) can find it.
          role: 'textbox',
          'aria-label': 'rdw-editor',
          'aria-multiline': 'true',
        },
        // Return true to signal the event was handled (prevents default insert),
        // matching draft-js handlePastedText/handleDrop returning 'handled'.
        handleKeyDown: (view, event) => !!onKeyDown(event),
        handlePaste: (view, event) => {
          const clipboard = event.clipboardData;
          const text = clipboard ? clipboard.getData('text/plain') : '';
          const pastedHtml = clipboard ? clipboard.getData('text/html') : '';

          return !!onPaste(text, pastedHtml);
        },
        handleDrop: () => true,
        handlePasteFiles: () => true,
      },
      onUpdate: ({ editor: e }) => {
        const next = e.getHTML();
        lastHtmlRef.current = next;
        onChange(next);
      },
      onFocus: () => onFocus(),
      onBlur: () => onBlur(),
    });

    useImperativeHandle(
      ref,
      () => ({
        focus: () => editor && editor.commands.focus('end'),
        getHTML: () => (editor ? editor.getHTML() : html),
        getText: () => (editor ? editor.getText() : ''),
        setContent: (nextHtml) => editor && editor.commands.setContent(nextHtml || '', false),
        insertText: (text) => {
          if (!editor) {
            return;
          }
          // Multi-line inserts (chat macros, pasted replies) are newline-delimited; promote them to block
          // structure so the editor keeps the paragraphs instead of collapsing the whitespace. Single-line
          // inserts (emoji, coupon codes) stay inline at the caret.
          const content = typeof text === 'string' && text.includes('\n') ? DraftHelpers.textToDraftHtml(text) : text;
          editor.chain().focus().insertContent(content).scrollIntoView().run();
        },
        setPrediction: (text) => editor && editor.commands.setPredictionText(text),
        clearPrediction: () => editor && editor.commands.clearPredictionText(),
        commitPrediction: () => editor && editor.commands.commitPredictionText(),
        isCaretAtDocEnd: () => isCaretAtDocEnd(editor),
        getEditor: () => editor,
      }),
      // Only depends on `editor`; `html` is a pre-init fallback inside getHTML,
      // so keeping it here would needlessly recreate the handle on every keystroke.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [editor]
    );

    // Keep the editor in sync when the message html changes from OUTSIDE the
    // editor (ticket switch, macro inserted by another component, etc). We
    // compare against the last html the editor itself emitted/received — NOT
    // editor.getHTML() — because getHTML() normalizes markup (e.g. Link adds
    // target/rel), which would otherwise make this fire on every render and
    // clobber in-progress edits (dropping links/images).
    React.useEffect(() => {
      if (!editor) {
        return;
      }
      const incoming = html || '';
      if (incoming !== lastHtmlRef.current) {
        lastHtmlRef.current = incoming;
        editor.commands.setContent(incoming, false);
      }
    }, [html, editor]);

    return <EditorContent editor={editor} className={className} />;
  }
);

MessageEditor.displayName = 'MessageEditor';

export default MessageEditor;
